import { useTranslation } from 'react-i18next';
import { useRequiredParams } from 'app/router/params';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Descendant, Transforms } from 'slate';
import { Editable, ReactEditor, Slate } from 'slate-react';
import root from 'react-shadow';
import { useNavigate } from '@tanstack/react-router';
import request from 'shared/lib/request';
import { useEditor } from 'app/components/editor/useEditor';
import { CustomEditor } from 'slate/types/slate';
import { DesignEntry } from 'entities/document';
import useSelection from 'app/components/editor/useSelection';
import withPage from 'slate/extensions/page';
import withParagraph from 'slate/extensions/paragraph';
import withLayout from 'slate/extensions/layout';
import { TemplateSidebar } from './TemplateSidebar';
import TemplateStructure from './TemplateStructure';
import { useDelayedSave } from 'app/utils/hooks/delayed-save';
import { NavbarTemplateEditor } from './navbar';
import { Spinner, SpinnerSmall } from 'app/components';
import { SubNavBar } from 'app/components/editor/navbar/SubNavbar';
import { SubToolbar } from 'app/components/editor/subtoolbar/SubToolbar';
import { v4 as uuidv4 } from 'uuid';
import { useUser } from 'app/state/contexts/UserContext';
import { useApplyTemplateDraft, useCompiledTemplate, useDocumentWithStyle, useSaveCompiledTemplate } from 'entities/template';
import { useDraft, useDiscardDraft, NO_DRAFT, scssErrorDetail } from 'entities/draft';
import { useCurrentUser } from 'entities/user';
import { EditorContext, EditorModeContextProvider } from 'app/state/contexts/EditorContext';
import { EditorScopeProvider } from 'app/state/contexts/EditorScopeContext';
import { useEditorDocumentOptional, useSetEditorDocument } from 'app/api/documents/useEditorData';
import { apiErrorText } from 'shared/api/error-message';
import { i18n } from 'shared/i18n';
import { shadowedCss as documentShadowedCss, exposedCss as documentExposedCss } from 'entities/document';

interface DraftEntry {
  type: string;
  style?: string;
  structure?: unknown;
  superId?: string;
  templateSuperId?: string;
  templateRevisionId?: string;
  [key: string]: unknown;
}

interface TemplateData {
  superId?: string;
  style?: string;
  structure?: unknown;
  className?: string;
  [key: string]: unknown;
}

interface TemplateDoc {
  superId: number | string;
  revisionId: string;
  css?: string;
  exposedCss?: string;
  content: { design: DesignEntry[] };
}

interface DocWithStyle {
  doc: TemplateDoc;
  style: { content: string };
}

const buildDraftContent = (draftContent: DraftEntry[] = [], documentDesign: DesignEntry, documentStyle: { content: string }, template: TemplateData) => {
  const content = [...draftContent];

  if (!content.find((s) => s.type === 'document')) {
    content.push({
      type: 'document',
      style: documentStyle.content,
      templateSuperId: documentDesign.templateSuperId,
      templateRevisionId: documentDesign.templateRevisionId,
    });
  }

  if (!content.find((s) => s.type === 'template' && s.templateSuperId === template.superId)) {
    content.push({
      ...template,
      templateSuperId: template.superId,
      type: 'template',
    });
  }

  return content;
};

const extensions = [withLayout({ markEmpty: true }), withPage(), withParagraph()];

interface StructureEditorProps {
  section?: Descendant[];
  onChange: (value: Descendant[], editor: CustomEditor) => void;
  extensions: Parameters<typeof useEditor>[0];
  leftComponent?: React.ReactNode;
  shadowedChildren?: React.ReactNode;
  className?: string;
  children?: React.ReactNode;
}

const StructureEditor = ({ section, onChange, extensions: shellExtensions, leftComponent, shadowedChildren, className, children }: StructureEditorProps) => {
  const editorContainerRef = useRef<HTMLDivElement | null>(null);
  const [shadowHost, setShadowHost] = useState<HTMLElement | null>(null);
  const editor = useEditor(shellExtensions);
  useSelection(editor, shadowHost);
  const { user: currentUser } = useCurrentUser()?.data || {};

  useEffect(() => {
    if (!!section) {
      editor.children = section;
    }
  }, [section, editor]);

  const handleChange = (value: Descendant[]) => {
    onChange(value, editor);

    if (editor.selection) {
      editor.lastSelection = editor.selection;
    }
  };

  const onEditableClick = (event: React.MouseEvent) => {
    try {
      ReactEditor.toSlateNode(editor, event.target as Node);
      return false;
    } catch {
      return true;
    }
  };

  if (section === undefined || !section?.length) {
    return null;
  }

  return (
    <div className={`editor-container ${className || ''}`} ref={editorContainerRef}>
      <EditorContext.Provider value={{ editorContainerRef, shadowHost }}>
        <Slate editor={editor} initialValue={section} onChange={handleChange}>
          {leftComponent}
          <root.div className="editor-shadow-host" ref={setShadowHost}>
            <Editable
              renderElement={editor.renderElement}
              onClick={onEditableClick}
              onKeyDown={(event: React.KeyboardEvent) => {
                if (event.key === 'Enter' && event.shiftKey) {
                  //Soft break on shift enter
                  event.preventDefault();
                  Transforms.insertText(editor, '\n');
                }
              }}
              renderLeaf={editor.renderLeaf}
              spellCheck={(currentUser?.userProperties as { spellCheck?: boolean } | undefined)?.spellCheck || false}
              className="editor"
            />
            {shadowedChildren}
            <style type="text/css">{`
              @media screen {
              .editor {
                position: relative;
                display: grid;
              }
              }
              .editor {
                padding-top: 60px;
                row-gap: 60px;
                z-index: 1;
              }

              .editor:focus {
                outline: none;
              }

              .layout-leaf-comment {
                background: #ffd56e;
              }

              .editor-image--container,
              .editor-table--container {
                position: relative;
              }
              .editor-image--container .controls--container,
              .editor-table--container .controls--container {
                position: absolute;
                display: flex;
                flex-direction: row;
                top: 5px;
                right: 5px;
                background: #f2f2f2;
                border-radius: 4px;
                box-shadow: 1px 1px 2px black;
                transition: all 300ms ease-in-out;
                opacity: 0;
                padding: 2px;
              }
              .editor-image--container .controls--container button,
              .editor-table--container .controls--container button {
                background: transparent;
                height: 30px;
                width: 30px;
                border: 0;
                cursor: pointer;
                color: red;
              }
              .editor-image--container:hover .controls--container,
              .editor-table--container:hover .controls--container {
                opacity: 1;
              }
            `}</style>
          </root.div>
          {children}
        </Slate>
      </EditorContext.Provider>
    </div>
  );
};

const templateEditorStyles = `
.layout-leaf-empty {
  background: rgba(193, 210, 228, 0.23);
}

.layout-leaf {
  outline: 1px dotted rgba(77, 184, 148, 0.5);
}

/*TODO lägg till .layout-styling på hover-effekt*/
}`;

const EditorDocumentScope = ({ doc, children }: { doc: TemplateDoc; children: React.ReactNode }) => {
  const setEditorDocument = useSetEditorDocument();
  const { data } = useEditorDocumentOptional();

  useEffect(() => {
    setEditorDocument(doc);
  }, [doc, setEditorDocument]);

  return data ? <>{children}</> : null;
};

export const TemplateEditor = () => {
  const { t } = useTranslation(['common', 'template-editor']);

  const navigate = useNavigate({ from: '/documents/$documentId/templates/$templateId' });

  const { documentId, templateId } = useRequiredParams('documentId', 'templateId');
  const { selectedOrganization } = useUser();

  // --- Query hooks ---
  const { data: templateData } = useCompiledTemplate(templateId, selectedOrganization, documentId);
  const { data: docWithStyle, refetch: refetchDocWithStyle } = useDocumentWithStyle(documentId, templateId) as {
    data?: DocWithStyle;
    refetch: () => Promise<{ data?: DocWithStyle }>;
  };
  const { data: draftData } = useDraft({ documentSuperId: documentId });

  // --- Derived from queries (no state needed) ---
  const doc = docWithStyle?.doc;

  const baseDraftContent = useMemo(() => {
    if (!docWithStyle || !templateData || !draftData) return null;
    const draft = draftData === NO_DRAFT ? null : draftData;
    const documentDesign = docWithStyle.doc.content.design.find((s: DesignEntry) => s.type === 'document');
    return buildDraftContent(
      (draft as { content?: DraftEntry[] } | null)?.content,
      documentDesign as DesignEntry,
      docWithStyle.style,
      templateData as TemplateData
    );
  }, [docWithStyle, templateData, draftData]);

  const documentStyle = useMemo(() => {
    if (!baseDraftContent) return null;
    return baseDraftContent.find((s) => s.type === 'document')?.style;
  }, [baseDraftContent]);

  // --- Local editor state (user edits these) ---
  const [template, setTemplate] = useState<TemplateData | null>(null);
  const [templateStyle, setTemplateStyle] = useState('');
  const [structureEditorContent, setStructureEditorContent] = useState<Descendant[] | undefined>();
  const [transpiledCss, setTranspiledCss] = useState('');
  const [exposedCss, setExposedCss] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [shouldStoreDraft, setShouldStoreDraft] = useState(false);
  const [draftExists, setDraftExists] = useState(false);
  const [dirtyDraft, setDirtyDraft] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);
  const [isApplying, setIsApplying] = useState(false);

  // --- Derived: draftContent from base + local edits (replaces 3 sync effects) ---
  const draftContent = useMemo(() => {
    if (!baseDraftContent || !structureEditorContent) return null;
    return baseDraftContent.map((entry) => {
      if (entry.type === 'template' && (entry.superId === templateId || entry.templateSuperId === templateId)) {
        return { ...entry, style: templateStyle, structure: structureEditorContent[0] };
      }
      return entry;
    });
  }, [baseDraftContent, templateStyle, structureEditorContent, templateId]);

  // --- Mutations ---
  const { mutate: applyTemplateDraft } = useApplyTemplateDraft(documentId);
  const { mutate: saveCompiledTemplate } = useSaveCompiledTemplate(documentId);
  const { mutate: discardDraftMutation } = useDiscardDraft();

  // --- Initialize local state from query data (only on first load or after reset) ---
  const initKeyRef = useRef<string | null>(null);
  const [resetKey, setResetKey] = useState(0);
  const currentInitKey = `${documentId}-${templateId}-${resetKey}`;

  useEffect(() => {
    if (!baseDraftContent || !templateData || !docWithStyle) return;
    if (initKeyRef.current === currentInitKey) return;
    initKeyRef.current = currentInitKey;

    const pDocument = docWithStyle.doc;
    const draft = draftData === NO_DRAFT ? null : draftData;

    setTranspiledCss(pDocument.css ?? '');
    setExposedCss(pDocument.exposedCss ?? '');
    setTemplate(templateData as TemplateData);
    setDraftExists(!!draft);

    const templateDraft = baseDraftContent.find((s) => s.type === 'template' && (s.superId === templateId || s.templateSuperId === templateId));
    setStructureEditorContent([templateDraft?.structure] as Descendant[]);
    setTemplateStyle(templateDraft?.style ?? '');
  }, [baseDraftContent, templateData, docWithStyle, draftData, templateId, currentInitKey]);

  // --- Draft saving ---
  const [storeDraft, cancelStoreDraft] = useDelayedSave(
    useCallback(
      async (draftContent: DraftEntry[] | null) => {
        if (!doc) return;
        const draft = {
          documentSuperId: doc.superId,
          documentRevisionId: doc.revisionId,
          content: draftContent,
          organization: selectedOrganization,
        };

        await request.post(`/gaby/design-draft?compiled=true`, draft);
        setDraftExists(true);
        setDirtyDraft(false);

        const { data } = await refetchDocWithStyle();
        if (data?.doc?.css) {
          setTranspiledCss(data.doc.css);
          setExposedCss(data.doc.exposedCss ?? '');
          setErrorMessage(null);
        } else {
          setErrorMessage('Error compiling sass');
        }
      },
      [selectedOrganization, doc, refetchDocWithStyle]
    )
  );

  useEffect(() => {
    if (shouldStoreDraft) {
      storeDraft(draftContent);
      setDirtyDraft(true);
      setShouldStoreDraft(false);
    }
  }, [draftContent, shouldStoreDraft, storeDraft]);

  // --- Handlers ---
  const handleTemplateClassNameChange = useCallback(
    (className: string) => {
      const newTemplate = {
        ...template,
        className: className.split(' ')[0],
      };
      setTemplate(newTemplate);
      setShouldStoreDraft(true);
    },
    [template]
  );

  const handleStructureChange = (value: Descendant[], editor: CustomEditor) => {
    if (editor.operations.some((op) => op.type !== 'set_selection')) {
      setShouldStoreDraft(true);
    }
    setStructureEditorContent(value);

    // FIXME This is needed for change events to happen, why?
    navigate({ replace: true, hash: '' });
  };

  const handleTemplateStyleChange = (style: string) => {
    setTemplateStyle(style);
    setShouldStoreDraft(true);
  };

  const handleDiscardDraftClick = async () => {
    cancelStoreDraft();
    setShouldStoreDraft(false);
    setIsDiscarding(true);
    setResetKey((k) => k + 1);
    discardDraftMutation(
      { superId: documentId },
      {
        onSuccess: () => {
          refetchDocWithStyle();
          setDraftExists(false);
          setIsDiscarding(false);
        },
        onError: () => {
          setIsDiscarding(false);
        },
      }
    );
  };

  const handleApplyDraftInProjectClick = async () => {
    if (!doc) return;
    setShouldStoreDraft(false);
    setIsApplying(true);
    const newTemplate = {
      ...template,
      style: templateStyle,
      structure: structureEditorContent?.[0],
      revisionId: uuidv4(),
    };

    saveCompiledTemplate(newTemplate, {
      onSuccess: (savedTemplate) => {
        setErrorMessage(null);
        setTemplate(savedTemplate as TemplateData);
        navigate({ replace: true, search: {} });

        applyTemplateDraft([doc.revisionId, templateId], {
          onSuccess: (response) => {
            const { css, shadowedCss: shadowed, exposedCss: exposed } = response.data;
            setTranspiledCss(documentShadowedCss(css) || shadowed || '');
            setExposedCss(documentExposedCss(css, exposed));
            setDraftExists(false);
            setIsApplying(false);
          },
          onError: () => {
            setIsApplying(false);
          },
        });
      },
      onError: async (e: unknown) => {
        setErrorMessage(scssErrorDetail(e) ?? apiErrorText(e, i18n('server:error.undefined.message')));
        setIsApplying(false);
        navigate({ replace: true, search: {} });
      },
    });
  };

  // --- Loading state ---
  if (!doc || !structureEditorContent || documentStyle === undefined || documentStyle === null) {
    return <Spinner />;
  }

  const shadowedChildren = <style type="text/css">{transpiledCss + templateEditorStyles}</style>;
  const permissions = { styling: true, insert: false, languange: false };
  const showSubToolbar = Object.values(permissions).some((value) => value); // Permission check?
  const toolbarComponents = { styling: true, insert: false, languange: false };

  return (
    <EditorScopeProvider scope={{ documentId, documentRevisionId: doc.revisionId, useDraft: true }}>
      <EditorDocumentScope doc={doc}>
        <EditorModeContextProvider>
          <div className={`container-fluid ps-0 ${showSubToolbar ? 'sub-navbar-padding' : ''}`}>
            <style type="text/css">{exposedCss}</style>
            <div className="d-flex">
              <StructureEditor
                className="d-flex w-100"
                extensions={extensions}
                onChange={handleStructureChange}
                section={structureEditorContent}
                shadowedChildren={shadowedChildren}
                leftComponent={<TemplateStructure template={template ?? {}} onTemplateClassNameChange={handleTemplateClassNameChange} />}
              >
                <NavbarTemplateEditor document={doc} template={template as { name?: string } | null} />
                <TemplateSidebar errorMessage={errorMessage} style={templateStyle} onStyleChange={handleTemplateStyleChange} isSaving={dirtyDraft}>
                  {draftExists && (
                    <div className="bg-light p-3 border border-gray-300 mt-r-2-5">
                      <div className="font-medium-header pb-r-1">{t('template-editor:draft-changes')}</div>
                      <button className="btn btn-sm btn-secondary me-r-1" type="button" disabled={isDiscarding || isApplying} onClick={handleDiscardDraftClick}>
                        {isDiscarding ? <SpinnerSmall /> : t('template-editor:button.discard-draft')}
                      </button>
                      <button
                        className="btn btn-sm btn-secondary"
                        type="button"
                        disabled={dirtyDraft || isApplying || isDiscarding}
                        onClick={handleApplyDraftInProjectClick}
                      >
                        {isApplying ? (
                          <>
                            <SpinnerSmall /> {t('common:saving')}
                          </>
                        ) : (
                          t('template-editor:button.update-project')
                        )}
                      </button>
                    </div>
                  )}
                </TemplateSidebar>
                <SubNavBar visibile={showSubToolbar}>
                  <SubToolbar enabled={toolbarComponents} />
                </SubNavBar>
              </StructureEditor>
            </div>
          </div>
        </EditorModeContextProvider>
      </EditorDocumentScope>
    </EditorScopeProvider>
  );
};
